import {useContext, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomImage, {FETCHPRIORITY} from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import GalleryBasic, {COUNTER_POSITIONS, SLIDER_IMAGE_DESIGNS, SLIDER_IMAGE_SIZES} from '@adv-mt/mt-gallery-basic'
import {
  imageUrlAdapterImageTypes,
  imageUrlAdapterTransformTypes,
  useImageUrlAdapter
} from '@adv-mt/mt-hook-use-image-url-adapter'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {useAnalyticsTrack as useAnalyticsTrackHook} from '@adv-mt/mt-tracking-use-analytics'

import CardAdContactSeller from './card-ad-contact-seller.js'

export {FETCHPRIORITY}

const ASPECT_RATIO = 1.33
const DEFAULT_PHOTO = 'images/no-photo-ad-card.svg'
const DESKTOP_WIDTH = 252
const MOBILE_WIDTH = 560
const NUMBER_OF_IMAGES_TO_PRELOAD = 2
const NO_OP = () => null

const CardAdMedia = ({
  ad,
  fetchpriority,
  hasLastImageContactButton,
  images = [],
  imageSize = SLIDER_IMAGE_SIZES.SMALL,
  isGridLayout = false,
  isPreview,
  showImageCounter = true,
  showImageSlider,
  title,
  url
}) => {
  const [isLoadedInClient, setIsLoadedInClient] = useState(false)

  const videoApiRef = useRef(null)
  const {push} = useRouter()
  const getImagesUrlsAdapted = useImageUrlAdapter()
  const {domain} = useContext(Context)
  const useAnalyticsTrack = useAnalyticsTrackHook || (() => ({track: NO_OP}))
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const urlStatics = config.get('urlStatics')

  const baseClassNames = cx('mt-CardAd-media', {
    'mt-CardAd-media--blured': !isLoadedInClient
  })

  useMount(() => {
    setIsLoadedInClient(true)
  })

  const initialMediaQueryMatch = {
    defaultMatches: !isMobile
  }
  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, initialMediaQueryMatch)

  const defaultPhotoSrc = `${urlStatics}${DEFAULT_PHOTO}`
  const meta = {
    alt: title,
    title
  }
  const hasImages = Boolean(images.length)

  const imageWidth = isGridLayout || !isHigherThanMobile ? MOBILE_WIDTH : DESKTOP_WIDTH

  const aspectRatioHeight = Math.floor(imageWidth / ASPECT_RATIO)
  const cardSize = {height: aspectRatioHeight, width: imageWidth}

  const errorIcon = <img height={aspectRatioHeight} src={defaultPhotoSrc} width={imageWidth} />

  const getContactCard = ({backgroundImage} = {}) => (
    <CardAdContactSeller ad={ad} backgroundImage={backgroundImage} cardSize={cardSize} />
  )

  const _addContactButton = mediaItems => {
    if (hasLastImageContactButton) {
      if (!hasImages) return [...mediaItems, getContactCard()]

      const lastImage = [...mediaItems].pop()

      const {
        images: [resizedLastImage]
      } = getImagesUrlsAdapted({
        images: [lastImage],
        options: {
          imageWidth,
          imageType: imageUrlAdapterImageTypes.GALLERY,
          transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
        }
      })

      return [...mediaItems.slice(0, 10), getContactCard({backgroundImage: resizedLastImage})]
    }

    return mediaItems
  }

  const _addVideo = mediaItems => {
    if (ad.videos === undefined || ad.videos.length === 0) {
      return mediaItems
    }

    const video = {
      isVideo: true,
      ...ad.videos[0],
      title: `Vídeo ${ad.title}`
    }

    const mediaItemsWithVideo = [
      ...(video.position === 'FIRST' ? [video] : []),
      ...mediaItems,
      ...(video.position === 'LAST' ? [video] : [])
    ]

    return mediaItemsWithVideo
  }

  const getMediaItemsArray = () => {
    let mediaItems = _getSliderImages()
    mediaItems = _addContactButton(mediaItems)
    mediaItems = _addVideo(mediaItems)
    return mediaItems
  }

  const hasVideo = ad.videos.length > 0
  const _getSliderImages = () =>
    hasImages
      ? images.map((imageSrc, index) => ({
          src: imageSrc,
          ...(index === 0 && {fetchpriority}),
          ...cardSize,
          ...meta
        }))
      : [{fetchpriority, src: defaultPhotoSrc, ...meta}]

  const mediaItemsArray = getMediaItemsArray()

  const [firstMediaItem] = mediaItemsArray
  const {
    images: [resizedImage]
  } = getImagesUrlsAdapted({
    images: [firstMediaItem],
    options: {
      imageWidth,
      imageType: imageUrlAdapterImageTypes.GALLERY,
      transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
    }
  })

  const enableCounter = showImageCounter && (hasImages || hasVideo)

  const getCounterPosition = () => {
    return COUNTER_POSITIONS.bottomLeft
  }
  const counterPosition = getCounterPosition()

  const onVideoClick = () => {
    if (isPreview) return

    const currentTime = videoApiRef.current?.getCurrentTime() || Promise.resolve(null)
    currentTime.then(videoTime => {
      push({
        pathname: url,
        query: {},
        state: {videoPlay: true, videoTime}
      })
    })
  }

  const onVideoPlay = async () => {
    const {
      TRACKING_EVENT_NAMES: {AD_VIDEO_PLAYED}
    } = config.get('tracking')
    if (ad.videos === undefined || ad.videos.length === 0) {
      return
    }

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      event: AD_VIDEO_PLAYED,
      origin: 'listado de coches'
    })

    track(eventData)
  }

  return (
    <div className={baseClassNames}>
      {showImageSlider ? (
        <GalleryBasic
          counterPosition={counterPosition}
          enableSlideByClick={false}
          enableCounter={enableCounter}
          id={ad.id}
          images={mediaItemsArray}
          isGridLayout={isGridLayout}
          itemsToPreload={NUMBER_OF_IMAGES_TO_PRELOAD}
          onImageloaded={() => setIsLoadedInClient(true)}
          onVideoClick={onVideoClick}
          onVideoPlay={onVideoPlay}
          showThumbnails={false}
          sliderImageDesign={SLIDER_IMAGE_DESIGNS.squared}
          sliderImageSize={imageSize}
          videoApiRef={videoApiRef}
        />
      ) : (
        <AtomImage
          className="mt-CardAd-mediaImage"
          errorIcon={errorIcon}
          fetchpriority={fetchpriority}
          height={resizedImage.height}
          src={resizedImage.src}
          width={resizedImage.width}
          {...meta}
        />
      )}
    </div>
  )
}

CardAdMedia.displayName = 'CardAdMedia'

CardAdMedia.propTypes = {
  ad: PropTypes.object,
  fetchpriority: PropTypes.oneOf(Object.values(FETCHPRIORITY)),
  hasLastImageContactButton: PropTypes.bool,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  imageSize: PropTypes.oneOf(Object.values(SLIDER_IMAGE_SIZES)),
  isExternalTarget: PropTypes.bool,
  isGridLayout: PropTypes.bool,
  isPreview: PropTypes.bool,
  linkFactory: PropTypes.func,
  onAdClick: PropTypes.func,
  showImageCounter: PropTypes.bool,
  showImageSlider: PropTypes.bool,
  title: PropTypes.string,
  url: PropTypes.string
}

export {SLIDER_IMAGE_SIZES as cardAdMediaImageSizes}

export default CardAdMedia
